/**
 * Export Menu Component
 * 
 * Provides options to export trip data in various formats
 */

import { useState } from 'react';
import { View, Modal, Pressable, ActivityIndicator, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body } from '@/components/ui/Text';
import type { Trip } from '@/services/trips';
import type { Expense } from '@/services/expenses';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem } from '@/services/itinerary';
import type { TripBudgetSnapshot } from '@/types/budget';
import {
  exportExpensesToCSV,
  exportTripToiCal,
  exportTripSummaryToHTML,
  addTripToDeviceCalendar,
  copyShareableLink,
} from '@/utils/export';

type ExportMenuProps = {
  visible: boolean;
  onClose: () => void;
  trip: Trip;
  expenses: Expense[];
  bookings: Booking[];
  itineraryItems: ItineraryItem[];
  /** When set, trip summary budget totals match Wallet BudgetSummary (converted FX). */
  budgetSnapshot?: TripBudgetSnapshot;
};

type ExportOption = {
  id: string;
  label: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => Promise<void>;
};

export function ExportMenu({
  visible,
  onClose,
  trip,
  expenses,
  bookings,
  itineraryItems,
  budgetSnapshot,
}: ExportMenuProps) {
  const theme = useTheme();
  const [exporting, setExporting] = useState<string | null>(null);

  const handleExport = async (exportFn: () => Promise<void>, optionId: string) => {
    if (exporting) return; // Prevent multiple simultaneous exports
    
    setExporting(optionId);
    try {
      await exportFn();
      // Close modal after a short delay to show success feedback
      // For link copy, close immediately; for file exports, wait a bit
      setTimeout(() => {
        setExporting(null);
        onClose();
      }, optionId === 'link' ? 300 : 800);
    } catch (error) {
      console.error('[ExportMenu] Export error:', error);
      setExporting(null);
      // Don't close on error so user can see the error message
      // Error alert is shown by the export function
    }
  };

  const exportOptions: ExportOption[] = [
    {
      id: 'csv',
      label: 'Export Expenses (CSV)',
      description: 'Download expenses and bookings as a CSV file',
      icon: 'document-text-outline',
      onPress: () => exportExpensesToCSV(trip, expenses, bookings),
    },
    {
      id: 'html',
      label: 'Export Trip Summary (HTML)',
      description: 'Generate a complete trip summary with all details',
      icon: 'document-outline',
      onPress: () =>
        exportTripSummaryToHTML(trip, expenses, bookings, itineraryItems, budgetSnapshot),
    },
    {
      id: 'ical',
      label: 'Export Calendar (iCal)',
      description: 'Add trip and itinerary to your calendar',
      icon: 'calendar-outline',
      onPress: () => exportTripToiCal(trip, itineraryItems),
    },
    ...(Platform.OS === 'web'
      ? []
      : [{
          id: 'native-calendar',
          label: 'Add to Device Calendar',
          description: Platform.OS === 'ios'
            ? 'Create events directly in Apple Calendar'
            : 'Create events directly in your phone calendar',
          icon: 'calendar-clear-outline' as keyof typeof Ionicons.glyphMap,
          onPress: () => addTripToDeviceCalendar(trip, itineraryItems),
        }]),
    {
      id: 'link',
      label: 'Copy Shareable Link',
      description: 'Copy a link to share this trip',
      icon: 'link-outline',
      onPress: () => copyShareableLink(trip.id),
    },
  ];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        style={{
          flex: 1,
          backgroundColor: theme.colors.modalOverlay,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing(2),
        }}
        onPress={onClose}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: 500,
          }}
        >
          <Card
            variant="elevated"
            style={{
              maxHeight: '100%', // give a bit more vertical room
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(3) }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5) }}>
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: theme.radii.md,
                    backgroundColor: theme.colors.primary + '20',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Ionicons name="download-outline" size={24} color={theme.colors.primary} />
                </View>
                <SubHeading>Export Trip</SubHeading>
              </View>
              <Pressable
                onPress={onClose}
                style={({ pressed }) => ({
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.sm,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </Pressable>
            </View>

            <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(2) }}>
              Choose how you'd like to export or share your trip data
            </Body>

            <View
              style={{
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.surface,
                borderWidth: 1.5,
                borderColor: theme.colors.border,
                overflow: 'hidden',
                alignSelf: 'stretch',
                maxWidth: '100%',
                flexShrink: 1,
              }}
            >
              {exportOptions.map((option, index) => {
                const isExporting = exporting === option.id;
                const isDisabled = exporting !== null && !isExporting;
                const isLast = index === exportOptions.length - 1;

                return (
                  <View key={option.id}>
                    <Pressable
                      onPress={() => !isDisabled && handleExport(option.onPress, option.id)}
                      disabled={isDisabled}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(2),
                        padding: theme.spacing(2),
                        minHeight: 72,
                        opacity: isDisabled ? 0.5 : pressed ? 0.7 : 1,
                        backgroundColor: 'transparent',
                      })}
                    >
                      <View
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: theme.radii.md,
                          backgroundColor: theme.colors.primary + '15',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {isExporting ? (
                          <ActivityIndicator size="small" color={theme.colors.primary} />
                        ) : (
                          <Ionicons name={option.icon} size={24} color={theme.colors.primary} />
                        )}
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <ThemedText 
                          variant="bodyStrong" 
                          style={{ 
                            marginBottom: theme.spacing(0.25),
                            fontSize: 15,
                            lineHeight: 20,
                          }}
                          numberOfLines={1}
                        >
                          {option.label}
                        </ThemedText>
                        <Body 
                          color={theme.colors.textSecondary} 
                          style={{ 
                            fontSize: 13,
                            lineHeight: 18,
                          }}
                          numberOfLines={2}
                        >
                          {option.description}
                        </Body>
                      </View>
                      {!isExporting && (
                        <View style={{ flexShrink: 0 }}>
                          <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
                        </View>
                      )}
                    </Pressable>
                    {!isLast && (
                      <View
                        style={{
                          height: 1,
                          backgroundColor: theme.colors.borderMuted,
                          marginLeft: 0,
                          marginRight: 0,
                        }}
                      />
                    )}
                  </View>
                );
              })}
            </View>
          </Card>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

